import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SitemapUrl {
  loc: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: number;
}

export const useSitemapGeneration = () => {
  const generateSitemap = async (): Promise<string> => {
    const baseUrls: SitemapUrl[] = [
      {
        loc: 'https://assurancedo.fr/',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 1.0
      },
      {
        loc: 'https://assurancedo.fr/guides',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.9
      },
      {
        loc: 'https://assurancedo.fr/faq',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.8
      },
      {
        loc: 'https://assurancedo.fr/contact',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.7
      },
      {
        loc: 'https://assurancedo.fr/a-propos',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.6
      },
      {
        loc: 'https://assurancedo.fr/mentions-legales',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'yearly',
        priority: 0.3
      },
      {
        loc: 'https://assurancedo.fr/politique-confidentialite',
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'yearly',
        priority: 0.3
      }
    ];

    // Get dynamic content from CMS
    try {
      const { data: guides } = await supabase
        .from('cms_content')
        .select('slug, updated_at, content_type')
        .eq('status', 'published');

      if (guides) {
        guides.forEach(guide => {
          const url: SitemapUrl = {
            loc: `https://assurancedo.fr/guides/${guide.slug}`,
            lastmod: new Date(guide.updated_at).toISOString().split('T')[0],
            changefreq: 'monthly',
            priority: guide.content_type === 'guide' ? 0.8 : 0.7
          };
          baseUrls.push(url);
        });
      }
    } catch (error) {
      console.error('Error fetching CMS content for sitemap:', error);
    }

    // Generate XML
    const xmlUrls = baseUrls.map(url => `
  <url>
    <loc>${url.loc}</loc>
    <lastmod>${url.lastmod}</lastmod>
    <changefreq>${url.changefreq}</changefreq>
    <priority>${url.priority}</priority>
  </url>`).join('');

    return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">${xmlUrls}
</urlset>`;
  };

  const updateSitemap = async () => {
    try {
      const sitemapContent = await generateSitemap();
      // Note: In a real implementation, you'd need a server endpoint to write this to public/sitemap.xml
      // For now, we'll log it for manual update or use a build-time generation
    } catch (error) {
      console.error('Error generating sitemap:', error);
    }
  };

  return { generateSitemap, updateSitemap };
};

// Hook to automatically update sitemap when content changes
export const useAutoSitemapUpdate = () => {
  const { updateSitemap } = useSitemapGeneration();

  useEffect(() => {
    // Update sitemap on component mount (page load)
    updateSitemap();
  }, []);

  return { updateSitemap };
};